import * as React from "react";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import { useLanguage } from "../../Language";
import { DatePicker } from "@mui/x-date-pickers";
import { useLocalizationContext } from "@mui/x-date-pickers/internals";

export interface DateOfBirthFieldProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  required: boolean;
}

export const DateOfBirthField = <T extends FieldValues>({
  name,
  label,
  required,
}: DateOfBirthFieldProps<T>) => {
  const adapter = useLocalizationContext();
  const { control } = useFormContext<T>();
  const { translate } = useLanguage();
  const now = adapter.utils.date(undefined);
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required && translate("isRequired", {
          LABEL: label
        }),
        validate: {
          validDate: (value) =>
            !value ||
            adapter.utils.isValid(value) ||
            translate("dateOfBirthIsInvalid"),
          pastDate: (value) =>
            !value ||
            !adapter.utils.isAfter(value, now) ||
            translate("disableFutureError"),
        },
      }}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { error },
      }) => (
        // Accepting/Closing the datepicker does not trigger a blur on TextField and thus does not trigger revalidation
        <DatePicker
          sx={{ maxWidth: { sm: "320px" } }}
          value={value}
          onChange={(date) => {
            if (date) {
              onChange(date);
            } else {
              onChange(null);
            }
          }}
          onClose={() => {            
            onBlur();
          }}
          inputRef={ref}
          slotProps={{
            textField: {
              onBlur: onBlur,
              helperText: error ? error.message : " ",
              required: !!required,
              error: !!error,
            },
          }}
          disableFuture
          label={label}
        />
      )}
    />
  );
};
